import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, AlertTriangle, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PlanillaForm } from "@/components/planillas/PlanillaForm";
import { api, apiErr } from "@/lib/api";
import { queuePlanilla } from "@/lib/offline";

export default function PlanillaFill() {
  const loc = useLocation();
  const nav = useNavigate();
  const st = loc.state || {};
  const tpl = st.template;
  const locationId = st.location_id;
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!tpl) { nav(-1); return null; }

  const onFinish = async ({ answers, anyIncident, caNote }) => {
    const payload = {
      template_id: tpl.id, location_id: locationId, area_id: (tpl.area_ids || [])[0] || null,
      shift: tpl.shift || null, corrective_note: anyIncident ? caNote : null, answers,
    };
    if (!navigator.onLine) {
      await queuePlanilla(payload);
      setResult({ offline: true, status: anyIncident ? "INCIDENT" : "COMPLETED" });
      toast.success("Guardado sin conexión", { description: "Pendiente de sincronización." });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/planillas/submissions", payload);
      setResult({ status: data.status, id: data.submission.id });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (result) {
    const inc = result.status === "INCIDENT";
    return (
      <div className="mx-auto max-w-md px-4 pb-24 pt-10 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className={`rounded-2xl border p-6 ${inc ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
          {inc ? <AlertTriangle className="mx-auto h-12 w-12 text-red-600" /> : <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />}
          <p className="mt-3 font-display text-xl font-semibold" data-testid="planilla-result">{inc ? "Planilla con incidencia" : "Planilla completada"}</p>
          {result.offline && <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600"><CloudOff className="h-3.5 w-3.5" /> Pendiente de sincronización</p>}
        </motion.div>
        <Button data-testid="planilla-done-btn" onClick={() => nav(-1)} className="mt-5 h-12 w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">Finalizar</Button>
      </div>
    );
  }

  return (
    <PlanillaForm template={tpl} mode="fill" submitting={saving} onFinish={onFinish}
      topSlot={<button onClick={() => nav(-1)} className="mb-3 inline-flex items-center gap-1 text-sm text-[#68756F]"><ArrowLeft className="h-4 w-4" /> Volver</button>} />
  );
}
